import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, Input, Text } from '@plurall/elo-beta'

import {
  CHANGE_EMAIL_BY_CODE,
  CHECK_EMAIL,
  CHECK_EMAIL_CODE,
  REQUEST_EMAIL_CODE,
} from 'graphql/mutations'
import { graphqlClient, handleGraphQLErrors, handleGraphQLErrorsWithToast } from 'utils'
import { useDebounce } from 'hooks'

import {
  hideUserInformation,
  renderToastSuccess,
  renderValidationText,
} from '../../utils/functions'
import { MAXIMUM_CODE_LENGTH, MAXIMUM_EMAIL_LENGTH, MESSAGES } from '../../utils/constants'

import styles from '../../PersonalData.module.css'

const DialogEditEmail = ({ openConfirm, onClose, setEmail }) => {
  const [errorNewEmail, setErrorNewEmail] = useState(null)
  const [errorConfirmEmail, setErrorConfirmEmail] = useState(null)

  const [newEmail, setNewEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')

  const [isLoadingEmail, setIsLoadingEmail] = useState(false)
  const [isLoadingSendEmail, setIsLoadingSendEmail] = useState(false)

  const checkEmail = async value => {
    try {
      setIsLoadingEmail(true)

      await graphqlClient().mutate({ mutation: CHECK_EMAIL, variables: { email: value || '' } })
      setErrorNewEmail(null)
    } catch (error) {
      if (error.graphQLErrors) {
        setErrorNewEmail(handleGraphQLErrors(error.graphQLErrors))
      }
    } finally {
      setIsLoadingEmail(false)
    }
  }

  const sendEmailCode = async () => {
    try {
      setIsLoadingSendEmail(true)

      await graphqlClient().mutate({ mutation: REQUEST_EMAIL_CODE, variables: { newEmail } })
      setEmail(newEmail)
      openConfirm()
    } catch (error) {
      handleGraphQLErrorsWithToast(
        error,
        'Não foi possível enviar o código. Tente novamente mais tarde.',
      )
    } finally {
      setIsLoadingSendEmail(false)
    }
  }

  const onSubmit = () => {
    if (newEmail !== confirmEmail) {
      setErrorConfirmEmail(MESSAGES.different_email)
    } else {
      setErrorConfirmEmail(null)
      sendEmailCode()
    }
  }

  const debounceNewEmail = useDebounce(checkEmail, 300)

  const onChangeNewEmail = value => {
    setNewEmail(value)
    debounceNewEmail(value)
  }

  const handlePaste = e => {
    e.preventDefault()
  }

  const isDisabled = !newEmail || !confirmEmail || !!errorNewEmail

  return (
    <Dialog
      title='Editar e-mail cadastrado'
      primaryButtonText='Continuar'
      onPrimaryClick={onSubmit}
      onClose={onClose}
      primaryButtonDisabled={isDisabled}
      primaryButtonLoading={isLoadingSendEmail}
      closePrimaryClick={false}
    >
      <Input
        placeholder='Insira o e-mail'
        onChange={onChangeNewEmail}
        dataTestId='input-newEmail'
        maxLength={MAXIMUM_EMAIL_LENGTH}
        {...renderValidationText(errorNewEmail, 'negative', errorNewEmail)}
        {...renderValidationText(isLoadingEmail, 'informative', MESSAGES.checking_email)}
      />
      <Input
        placeholder='Repita o e-mail'
        onChange={e => setConfirmEmail(e)}
        dataTestId='input-confirmEmail'
        onPaste={handlePaste}
        maxLength={MAXIMUM_EMAIL_LENGTH}
        {...renderValidationText(errorConfirmEmail, 'negative', errorConfirmEmail)}
      />
    </Dialog>
  )
}

const DialogConfirmEmail = ({ backToEdit, onClose, email, setUpdatedEmail }) => {
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState(null)
  const [showBtnResend, setShowBtnResend] = useState(true)
  const [isLoadingCheckCode, setIsLoadingCheckCode] = useState(false)
  const [isLoadingChangeEmail, setIsLoadingChangeEmail] = useState(false)

  const handleBtnResend = () => {
    setShowBtnResend(false)
    setTimeout(() => {
      setShowBtnResend(true)
    }, 60000)
  }

  const checkCode = async () => {
    if (code) {
      try {
        setIsLoadingCheckCode(true)
        setIsLoadingChangeEmail(true)

        await graphqlClient().mutate({
          mutation: CHECK_EMAIL_CODE,
          variables: { email, code: Number(code) },
        })
        setCodeError(null)
        changeEmail()
      } catch (error) {
        if (error.graphQLErrors) {
          setCodeError(handleGraphQLErrors(error.graphQLErrors))
        }
      } finally {
        setIsLoadingCheckCode(false)
        setIsLoadingChangeEmail(false)
      }
    }
  }

  const changeEmail = async () => {
    await graphqlClient().mutate({
      mutation: CHANGE_EMAIL_BY_CODE,
      variables: { email, code: Number(code) },
    })

    setUpdatedEmail(email)
    onClose()
    renderToastSuccess()
  }

  const resendEmailCode = async () => {
    try {
      await graphqlClient().mutate({ mutation: REQUEST_EMAIL_CODE, variables: { newEmail: email } })
      handleBtnResend()
    } catch (error) {
      handleGraphQLErrorsWithToast(
        error,
        'Não foi possível enviar o código. Tente novamente mais tarde.',
      )
    }
  }

  const isDisabled = !code

  return (
    <Dialog
      title='Confirmação de e-mail'
      primaryButtonText='Continuar'
      secondaryButtonText='Voltar'
      onSecondaryClick={backToEdit}
      onClose={onClose}
      primaryButtonDisabled={isDisabled}
      onPrimaryClick={checkCode}
      closePrimaryClick={false}
      primaryButtonLoading={isLoadingChangeEmail || isLoadingCheckCode}
    >
      <div className={styles['dialog-info']}>
        <Text dimmed>
          Enviamos um código de segurança para o e-mail: {hideUserInformation(email)}
        </Text>
      </div>
      <Input
        placeholder='Insira o código recebido'
        type='number'
        hideSpinner
        onChange={e => setCode(e)}
        dataTestId='input-emailCode'
        maxLength={MAXIMUM_CODE_LENGTH}
        {...renderValidationText(codeError, 'negative', codeError)}
        {...renderValidationText(isLoadingCheckCode, 'informative', MESSAGES.checking_code)}
      />
      {showBtnResend && (
        <div className={styles['dialog-resend-code']}>
          <Text element='a' size='t3' onClick={resendEmailCode} dataTestId='btn-resend'>
            Reenviar código
          </Text>
        </div>
      )}
    </Dialog>
  )
}

DialogEditEmail.propTypes = {
  openConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  setEmail: PropTypes.func.isRequired,
}

DialogConfirmEmail.propTypes = {
  backToEdit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  setUpdatedEmail: PropTypes.func.isRequired,
}

export { DialogEditEmail, DialogConfirmEmail }
